/** @format */

/**
 * Trace 分析 — 单 Trace 详情：路由 query 携带 serviceName、traceId、时间范围等。
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { _$, Card, dayjs, Tabs, Tab, Checkbox, Split, Tag, Icon } from '@king-design/react'
import { useLocation, useHistory } from 'react-router'
import { replaceSearchParams } from 'src/utils'
import {
  Descriptions,
  DescriptionItem,
  FormatCompactK,
  MonitorCrumb,
  MonitorSearch
} from 'src/components'
import { useAsync } from 'react-use'
import type { TraceAnalysisDetailResponse, TraceAnalysisDetailSpan } from '../typescript'
import { flattenTraceDetailSpans } from '../constant'
import { secondsFormat } from 'src/utils'
import TraceSpanTree from './TraceSpanTree'
import TraceSpanTopology from './TraceSpanTopology'
import TraceSpanDetail from './TraceSpanDetail'
import { getTraceDetail } from 'src/api/ApiAIObservability'
import { getDefaultSelectableSpan } from './topology'

/** 详情页 Token 展示：优先用 Trace 级汇总字段 */
function getTraceDetailTokens(detail: TraceAnalysisDetailResponse | undefined) {
  if (!detail) {
    return { input: 0, output: 0, total: 0 }
  }
  return {
    input: detail.InputTokens,
    output: detail.OutputTokens,
    total: detail.TotalTokens
  }
}

/** 解析当前 URL search，供详情请求参数使用 */
function useQuery() {
  return new URLSearchParams(useLocation().search)
}

type ViewType = 'list' | 'topology'

const TraceDetailPage: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const location = useLocation()
  const query = useQuery()
  const traceId = query.get('traceId')
  const from = query.get('from')
  const fromSessionId = query.get('sessionId')
  const viewType = query.get('viewType') || 'list'
  const backLockRef = useRef(false)

  const [tab, setTab] = useState<ViewType>(viewType as ViewType)
  // 筛选Span
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [searchValue, setSearchValue] = useState<string>('')
  const [showErrorSpans, setShowErrorSpans] = useState<boolean>(false)
  // 选中的 Span（切换 trace 或 Span 列表变化时校验是否仍存在）
  const [selectedSpan, setSelectedSpan] = useState<TraceAnalysisDetailSpan | null>(null)

  const { value: traceDetail } = useAsync(async (): Promise<
    TraceAnalysisDetailResponse | undefined
  > => {
    if (!traceId) {
      return undefined
    }
    const result = await getTraceDetail({ TraceId: traceId })
    if (!result?.TraceId) {
      return undefined
    }
    return {
      ...result,
      Spans: flattenTraceDetailSpans(result.Spans ?? [])
    }
  }, [traceId])

  useEffect(() => {
    if (!traceDetail?.Spans?.length || selectedSpan?.SpanId) {
      return
    }
    const defaultSpan = getDefaultSelectableSpan(traceDetail, traceDetail.Spans)
    if (defaultSpan) {
      setSelectedSpan(defaultSpan)
    }
  }, [traceDetail, selectedSpan])

  // 后退
  const handleBack = useCallback(() => {
    if (backLockRef.current) {
      return
    }
    backLockRef.current = true
    window.setTimeout(() => {
      backLockRef.current = false
    }, 350)

    if (from === 'session' && fromSessionId) {
      history.go(-1)
      return
    }
    history.goBack()
  }, [from, fromSessionId, history])

  // Token使用情况
  const tokenAgg = useMemo(() => getTraceDetailTokens(traceDetail), [traceDetail])

  // 标签
  const tags = useMemo(() => {
    const result = {}
    traceDetail?.Spans.forEach(span => {
      const kind = span.Kind
      if (result[kind]) {
        result[kind]++
      } else {
        result[kind] = 1
      }
    })
    return result
  }, [traceDetail])

  // 异常Span
  const errorSpans = useMemo(() => {
    return traceDetail?.Spans.filter(span => span.StatusCode === 'ERROR')
  }, [traceDetail])
  return (
    <div className={className} id="trace_detail_page">
      <MonitorCrumb
        breadArr={[
          { title: _$('AI可观测') },
          { title: _$('Trace分析'), route: '/monitor/aiobservability/traceanalysis' },
          { title: traceId }
        ]}
      />
      <Card
        slotTitle={
          <div className="k-card-title-back" onClick={handleBack}>
            <Icon className="ion-ios-arrow-back" />
            <span>{_$('Trace详情')}</span>
          </div>
        }
        className="trace-analysis-card"
      >
        <div className="trace-detail-desc">
          <Descriptions columns={3}>
            <DescriptionItem label={_$('服务名称')}>
              {traceDetail?.ServiceName ?? '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('开始时间')}>
              {traceDetail?.StartTime
                ? dayjs(traceDetail.StartTime).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('耗时')}>
              {secondsFormat(traceDetail?.DurationMs ?? 0)}
            </DescriptionItem>
            <DescriptionItem span={1} label={_$('Token使用情况')}>
              <div className="trace-detail-token-wrap">
                <div className="trace-detail-token-inline">
                  <span className="trace-detail-token-item">
                    {_$('Total Tokens')}：<FormatCompactK value={tokenAgg.total} />
                  </span>
                  <span className="trace-detail-token-sep" aria-hidden />
                  <span className="trace-detail-token-item">
                    {_$('输入Token')}：<FormatCompactK value={tokenAgg.input} />
                  </span>
                  <span className="trace-detail-token-sep" aria-hidden />
                  <span className="trace-detail-token-item">
                    {_$('输出Token')}：<FormatCompactK value={tokenAgg.output} />
                  </span>
                </div>
              </div>
            </DescriptionItem>
          </Descriptions>
        </div>
        <Tabs
          type="flat-card"
          value={tab}
          onChangeValue={v => {
            setTab(v as ViewType)
            replaceSearchParams(history, location, { viewType: v })
          }}
        >
          <Tab value="list">{_$('列表')}</Tab>
          <Tab value="topology">{_$('拓扑')}</Tab>
        </Tabs>

        <div className="trace-detail-header">
          <div className="header-tag-wrap">
            <p className="header-tag-label">
              {_$('大模型组件标签')}({Object.keys(tags)?.length})<span>{'>'}</span>
            </p>
            {Object.entries(tags).map(([key, value]) => {
              return (
                <Tag
                  key={key}
                  type={selectedTags.includes(key) ? 'primary' : 'default'}
                  onClick={() => {
                    if (selectedTags.includes(key)) {
                      setSelectedTags(selectedTags.filter(tag => tag !== key))
                    } else {
                      setSelectedTags([...selectedTags, key])
                    }
                  }}
                >
                  {key}({value})
                </Tag>
              )
            })}
          </div>
          <div className="header-search-wrap">
            <MonitorSearch
              value={searchValue}
              onChange={setSearchValue}
              placeholder={_$('输入Span关键字')}
            />
            <Checkbox
              disabled={errorSpans?.length === 0}
              value={showErrorSpans}
              onChangeValue={setShowErrorSpans}
            >
              {_$('查看异常Span')}({errorSpans?.length})
            </Checkbox>
          </div>
        </div>
        <Split
          firstSize="70%"
          lastSize="30%"
          slotFirst={
            tab === 'list' ? (
              <TraceSpanTree
                detail={traceDetail}
                selectedTags={selectedTags}
                searchValue={searchValue}
                showErrorSpans={showErrorSpans}
                selectedSpanId={selectedSpan?.SpanId ?? null}
                onSelectSpan={setSelectedSpan}
              />
            ) : selectedSpan ? (
              <TraceSpanTopology
                detail={traceDetail}
                selectedTags={selectedTags}
                searchValue={searchValue}
                showErrorSpans={showErrorSpans}
                selectedSpanId={selectedSpan?.SpanId ?? null}
                onSelectSpan={setSelectedSpan}
              />
            ) : null
          }
          slotLast={<TraceSpanDetail span={selectedSpan} sessionId={traceDetail?.SessionId} />}
        ></Split>
      </Card>
    </div>
  )
}

export default styled(TraceDetailPage)`
  padding: 0 16px;
  .trace-detail-span-tree-card {
    margin-top: 16px;
  }

  /* 顶部描述区 Token 横向展示 */
  .trace-detail-desc {
    .trace-detail-token-wrap {
      overflow-x: auto;
      overflow-y: hidden;
      word-break: normal;

      .trace-detail-token-inline {
        display: inline-flex;
        flex-wrap: nowrap;
        align-items: center;
        white-space: nowrap;
        gap: 0;
        min-width: min-content;

        .trace-detail-token-item {
          flex-shrink: 0;
          white-space: nowrap;
        }

        .trace-detail-token-sep {
          flex-shrink: 0;
          width: 1px;
          height: 14px;
          margin: 0 16px;
          background: rgba(0, 0, 0, 0.15);
        }
      }
    }
  }

  /* Span 列表筛选：Kind 标签 + 搜索 */
  .trace-detail-header {
    margin: 16px 0;
    .header-tag-wrap {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      gap: 16px;
      .header-tag-label {
        font-weight: 500;
        line-height: 20px;
        margin-bottom: 0;
        & > span {
          margin: 0 8px;
        }
      }
      .k-tag {
        cursor: pointer;
      }
    }
    .header-search-wrap {
      display: flex;
      align-items: center;
      gap: 16px;
      .k-checkbox-text {
        font-weight: 400;
        line-height: 24px;
      }
    }
  }

  .k-split {
    border: 1px solid #f0f0f0;
    border-radius: 4px;
  }
`
